import { useCallback, useEffect, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import {
  Badge, EmptyState, PageLoading, Toggle,
} from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';

import type { ProductAttribute, ProductAttributeKind } from '../../types';

/**
 * The category / brand / unit masters.
 *
 * Three lists that feed the product form's dropdowns. They exist so a
 * catalogue does not sprout "Biscuits" and "Biscuit" as two categories —
 * the product form picks from these, and this is where they are kept tidy.
 *
 * Deleting a name does not touch the products that already carry it (they
 * store the name as text): it only takes the name off the dropdown. The
 * screen says so, because "delete" here means less than an admin expects.
 */

const TABS: { kind: ProductAttributeKind; label: string; singular: string }[] = [
  { kind: 'category', label: 'Categories', singular: 'category' },
  { kind: 'brand', label: 'Brands', singular: 'brand' },
  { kind: 'unit', label: 'Units', singular: 'unit' },
];

export default function ProductMastersPage() {
  const { can } = useAuth();
  const toast = useToast();
  const isAdmin = can('company_admin', 'platform_admin');

  const [kind, setKind] = useState<ProductAttributeKind>('category');
  const [items, setItems] = useState<ProductAttribute[]>([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const tab = TABS.find((t) => t.kind === kind)!;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await api.get<ProductAttribute[]>('/orders/product-attributes', { kind });
      setItems(rows);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load the list.');
    } finally {
      setLoading(false);
    }
  }, [kind, toast]);

  useEffect(() => { void load(); }, [load]);

  async function add() {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      await api.post('/orders/product-attributes', { kind, name });
      setNewName('');
      toast.success(`Added ${name}.`);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not add that.');
    } finally {
      setBusy(false);
    }
  }

  async function rename(item: ProductAttribute) {
    const name = editName.trim();
    if (!name) return;
    if (name === item.name) { setEditingId(null); return; }
    setBusy(true);
    try {
      await api.patch(`/orders/product-attributes/${item.id}`, { name });
      setEditingId(null);
      toast.success('Renamed.');
      await load();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not rename that.');
    } finally {
      setBusy(false);
    }
  }

  async function setActive(item: ProductAttribute, isActive: boolean) {
    try {
      await api.patch(`/orders/product-attributes/${item.id}`, { isActive });
      setItems((list) => list.map((i) => (i.id === item.id ? { ...i, isActive } : i)));
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not update that.');
    }
  }

  async function remove(item: ProductAttribute) {
    setBusy(true);
    try {
      const result = await api.del<{ productsUsing: number; note?: string }>(
        `/orders/product-attributes/${item.id}`,
      );
      toast.success(result.note ?? `Removed ${item.name}.`);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not remove that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Product lists"
        subtitle="The categories, brands and units the product form offers. Keeping them tidy here keeps the app's filters tidy."
      />

      <div className="page col gap-16">
        <div className="tabs">
          {TABS.map((t) => (
            <button
              key={t.kind}
              type="button"
              className={`tab${t.kind === kind ? ' active' : ''}`}
              onClick={() => { setKind(t.kind); setEditingId(null); }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {isAdmin ? (
          <div className="card">
            <div className="row gap-8" style={{ padding: 14, alignItems: 'flex-end' }}>
              <div className="grow">
                <label className="field-label">Add a {tab.singular}</label>
                <input
                  className="input"
                  value={newName}
                  placeholder={kind === 'unit' ? 'e.g. crate' : kind === 'brand' ? 'e.g. Sunfeast' : 'e.g. Biscuits'}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void add(); }}
                />
              </div>
              <button type="button" className="btn btn-primary" disabled={busy || !newName.trim()}
                onClick={() => void add()}>
                <Icon name="plus" size={16} /> Add
              </button>
            </div>
          </div>
        ) : null}

        {loading ? <PageLoading /> : items.length === 0 ? (
          <div className="card">
            <EmptyState
              icon="list"
              title={`No ${tab.label.toLowerCase()} yet`}
              body={isAdmin ? `Add your first ${tab.singular} above, or they fill in as you tag products.`
                : 'Nothing here yet.'}
            />
          </div>
        ) : (
          <div className="card">
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>{tab.singular === 'category' ? 'Category' : tab.singular === 'brand' ? 'Brand' : 'Unit'}</th>
                    <th className="num">Products</th>
                    <th>Shown in form</th>
                    {isAdmin ? <th /> : null}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td className="primary">
                        {editingId === item.id ? (
                          <div className="row gap-8">
                            <input
                              className="input"
                              autoFocus
                              value={editName}
                              style={{ maxWidth: 240 }}
                              onChange={(e) => setEditName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') void rename(item);
                                if (e.key === 'Escape') setEditingId(null);
                              }}
                            />
                            <button type="button" className="btn btn-primary btn-sm" disabled={busy}
                              onClick={() => void rename(item)}>Save</button>
                            <button type="button" className="btn btn-secondary btn-sm"
                              onClick={() => setEditingId(null)}>Cancel</button>
                          </div>
                        ) : (
                          <>
                            {item.name}
                            {!item.isActive ? <> <Badge tone="neutral">Hidden</Badge></> : null}
                          </>
                        )}
                      </td>
                      <td className="num">{item.productCount ?? 0}</td>
                      <td>
                        {isAdmin ? (
                          <Toggle
                            checked={item.isActive}
                            label=""
                            onChange={(v) => void setActive(item, v)}
                          />
                        ) : (item.isActive ? 'Yes' : 'No')}
                      </td>
                      {isAdmin ? (
                        <td>
                          {editingId === item.id ? null : (
                            <div className="row gap-4" style={{ justifyContent: 'flex-end' }}>
                              <button type="button" className="icon-btn" title="Rename"
                                onClick={() => { setEditingId(item.id); setEditName(item.name); }}>
                                <Icon name="edit" size={15} />
                              </button>
                              <button type="button" className="icon-btn" title="Remove from list"
                                disabled={busy} onClick={() => void remove(item)}>
                                <Icon name="trash" size={15} />
                              </button>
                            </div>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
